import React, { useState } from 'react'
import { useParams } from 'react-router'
import { api } from '../utils/api'
import ThemeToggle from '../components/ThemeToggle'

const Unsubscribe = () => {
  const { token } = useParams()
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  const handleUnsubscribe = async () => {
    setLoading(true)
    try {
      const response = await api.post(`/auth/unsubscribe/${token}`)
      setResult(response.data)
    } catch (error) {
      setError(error.response?.data?.error || 'Er is een fout opgetreden')
    } finally {
      setLoading(false)
    }
  }

  // Success state
  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <div className="max-w-2xl w-full">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
            {/* Header */}
            <div className="px-6 py-8 text-center bg-green-50 dark:bg-green-900/30">
              <div className="text-6xl mb-4">✅</div>
              <h1 className="text-2xl font-bold mb-2 text-green-900 dark:text-green-100">
                Uitgeschreven
              </h1>
              <p className="text-lg text-green-700 dark:text-green-300">
                {result.message}
              </p>
            </div>

            {/* Actions */}
            <div className="px-6 py-6 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                <button
                  onClick={() => window.close()}
                  className="hover:text-gray-700 dark:hover:text-gray-200 hover:underline cursor-pointer transition-colors"
                >
                  Je kunt dit venster sluiten.
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <div className="max-w-md w-full text-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <div className="text-6xl mb-4">❌</div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Uitschrijven mislukt
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {error}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              Probeer opnieuw
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Confirmation state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <div className="max-w-2xl w-full">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="px-6 py-8 text-center bg-orange-50 dark:bg-orange-900/30">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold mb-2 text-orange-900 dark:text-orange-100">
              Uitschrijven bevestigen
            </h1>
            <p className="text-lg text-orange-700 dark:text-orange-300">
              Weet je zeker dat je je wilt uitschrijven?
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 mb-6">
              <div className="flex items-start">
                <div className="shrink-0">
                  <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                    Let op: Deze actie is onherroepelijk
                  </h3>
                  <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Al je gegevens worden permanent verwijderd</li>
                      <li>Je ontvangt geen meetup notificaties meer</li>
                      <li>Je registraties voor meetups worden geannuleerd</li>
                      <li>Je moet opnieuw registreren om weer mee te kunnen doen</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Actions */}
          <div className="px-6 py-6 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.close()}
                disabled={loading}
                className="px-6 py-3 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuleren
              </button>
              <button
                onClick={handleUnsubscribe}
                disabled={loading}
                className="px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Bezig met uitschrijven...
                  </>
                ) : (
                  'Ja, schrijf me uit'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Unsubscribe
